import { isDefined, isNotDefined } from '@sgme/fp';
import { Button, Icon, Tooltip } from '@sgme/ui';
import clsx from 'clsx';
import { FormattedMessage } from 'react-intl';

import { OptionFormGroup } from '@/App/tiles/Option/fields/OptionFormGroup.tsx';
import { useOptionSelector } from '@/App/tiles/Option/OptionContext.ts';
import { useCheckOption } from '@/App/tiles/Option/QueueContext.tsx';
import { SgAmountInput } from '@/components/sg-design-system/components/input/SgAmountInput';
import { selectOptionCurrency } from '@/store/option/selectors/optionSelectors.ts';

export function AmountReference({ tileId }: { tileId: string }) {
  const [checkOption] = useCheckOption();
  const { amountReference, amountReferenceCurrency, isAmountReferenceOverridden } = useOptionSelector((option) => {
    const { currency1, currency2 } = selectOptionCurrency(option);
    const premiumType = option.properties.premiumType;
    const currencyIndex = premiumType.premiumCurrency;
    const premiumCurrency = currencyIndex === 1 ? currency1 : currency2;
    return {
      amountReference: option.properties.amountReference,
      amountReferenceCurrency: option.properties.amountReferenceCurrency,
      isAmountReferenceOverridden: option.properties.isAmountReferenceOverridden,
      premiumCurrency,
    };
  });

  if (isNotDefined(amountReference) && isNotDefined(amountReferenceCurrency) && !isAmountReferenceOverridden) {
    return null;
  }

  function handleChange(amountReference: number | null | undefined): void {
    checkOption({ tileId, properties: { amountReference } });
  }

  function handleUnpin() {
    // FIXME: this does not work neither in forex nor in FX
    handleChange(null);
  }

  const isPinned = isDefined(amountReference);

  return (
    <OptionFormGroup
      label={<FormattedMessage id={'option.fields.amountReference'} />}
      addon={
        isPinned && (
          <Tooltip contents={<FormattedMessage id="option.pin.tooltip.message" />}>
            <Button
              onClick={handleUnpin}
              data-e2e="AmountReferenceNotionalInfo"
              size="sm"
              purpose="flat"
              iconPosition="icon-only"
            >
              <Icon size="sm" icon="push_pin" />
            </Button>
          </Tooltip>
        )
      }
    >
      <div className="d-flex">
        <SgAmountInput
          data-e2e="amountReference"
          acceleratorsEnabled
          selectAllOnFocus
          value={amountReference}
          onChange={handleChange}
          size="sm"
          unit={amountReferenceCurrency}
          className={clsx({
            'text-info': isPinned,
          })}
        />
      </div>
    </OptionFormGroup>
  );
}
